import posthog from 'posthog-js'
import { referralSource, registerAttribution } from './attribution'

declare global {
  interface Window {
    umami?: {
      track: (event: string, data?: Record<string, unknown>) => void;
      identify: (properties: Record<string, unknown>) => void;
    };
  }
}

const CONSENT_KEY = 'lo-consent'
const UMAMI_SITE_ID = '1955c196-681b-4bc2-8ca1-fc16efcd1320'

let _initialized = false

export type ConsentStatus = 'granted' | 'denied' | null

// localStorage can throw (locked-down Safari, quota) — never let consent
// plumbing crash a render.
function lsGet(key: string): string | null {
  try { return localStorage.getItem(key) } catch { return null }
}
function lsSet(key: string, value: string) {
  try { localStorage.setItem(key, value) } catch { /* tracking stays off */ }
}

// Mirror the consent choice into a cookie so the SERVER honours it too. Consent
// lives in localStorage (not sent with requests), but server-side product
// captures (decisions/feedback/agent/shares) must respect the same banner — the
// backend reads this cookie (analytics.analytics_consent). 1-year, Lax, not
// httpOnly (the client writes it). Transactional events (purchases) ignore it.
function setConsentCookie(value: 'granted' | 'denied') {
  try {
    const secure = window.location.protocol === 'https:' ? '; Secure' : ''
    document.cookie = `${CONSENT_KEY}=${value}; path=/; max-age=31536000; SameSite=Lax${secure}`
  } catch { /* cookies disabled — server just won't capture, which is the safe default */ }
}
function clearConsentCookie() {
  try { document.cookie = `${CONSENT_KEY}=; path=/; max-age=0; SameSite=Lax` } catch { /* ignore */ }
}

export function consentStatus(): ConsentStatus {
  if (navigator.doNotTrack === '1') return 'denied'
  const v = lsGet(CONSENT_KEY)
  return v === 'granted' || v === 'denied' ? v : null
}

export function grantConsent() {
  lsSet(CONSENT_KEY, 'granted')
  setConsentCookie('granted')
  initAnalytics()
}

export function denyConsent() {
  lsSet(CONSENT_KEY, 'denied')
  setConsentCookie('denied')
}

/** Withdraw consent (GDPR: as easy as granting). Reload tears down loaded trackers. */
export function resetConsent() {
  try { localStorage.removeItem(CONSENT_KEY) } catch { /* ignore */ }
  clearConsentCookie()
  if (_initialized) {
    try { posthog.opt_out_capturing() } catch { /* ignore */ }
  }
  window.location.reload()
}

/** No-op unless the user has granted analytics consent (and DNT is off). */
export function initAnalytics() {
  if (_initialized || consentStatus() !== 'granted') return

  // Backfill the consent cookie for users who granted before this cookie
  // existed (localStorage said granted, but the server never saw it). Idempotent.
  setConsentCookie('granted')

  const key = import.meta.env.VITE_POSTHOG_KEY
  if (key) {
    posthog.init(key, {
      // Route ingestion through our own origin (/ingest → reverse-proxied to
      // PostHog in app.py) so tracker blockers don't drop first-party requests.
      // ui_host keeps toolbar/links pointed at real PostHog.
      api_host: import.meta.env.VITE_POSTHOG_HOST ?? `${window.location.origin}/ingest`,
      ui_host: 'https://eu.posthog.com',
      person_profiles: 'identified_only',
      capture_pageview: true,
      capture_pageleave: true,
      autocapture: false,
      // Surveys aren't used here, and posthog-js's eager surveys.js fetch
      // ignores the remote "surveys": false config (posthog-js#2844) — a
      // version-mismatched build of that module throws on execution and
      // kills init before the capture queue ever flushes, silently
      // dropping every event site-wide. Verified live 2026-07-22.
      // disable_surveys only gates the PostHogSurveys class's post-load
      // decision to activate — it does NOT stop the network fetch, so it
      // doesn't prevent the crash (verified: still broken with only this
      // set). disable_external_dependency_loading is checked directly in
      // loadScript() before any external chunk is fetched — the actual
      // gate. No extension this app doesn't already have off (surveys,
      // session recording, exception autocapture, product tours) needs
      // external loading, so this has no functional cost here.
      disable_surveys: true,
      disable_external_dependency_loading: true,
      // Strip query strings from captured URLs so nothing sensitive in a link
      // (e.g. the /should-i-quit shareable-scenario params, which include
      // salary figures) ever reaches analytics. Keeps path-level insight only.
      sanitize_properties: (props) => {
        if (!props) return props
        for (const k of ['$current_url', '$referrer', '$initial_current_url', '$initial_referrer']) {
          const v = props[k]
          if (typeof v === 'string') props[k] = v.split(/[?#]/)[0]
        }
        return props
      },
      __add_tracing_headers: import.meta.env.VITE_API_HOST
        ? [import.meta.env.VITE_API_HOST]
        : undefined,
    })
    _initialized = true
    registerAttribution()
  }

  // Umami loads only after consent, first-party via our /u proxy (app.py) so
  // blockers don't drop it. data-host-url points the tracker's /api/send at the
  // same first-party path. No-ops if still blocked.
  const umamiSrc = `${window.location.origin}/u/script.js`
  if (!document.querySelector(`script[src="${umamiSrc}"]`)) {
    const s = document.createElement('script')
    s.defer = true
    s.src = umamiSrc
    s.dataset.websiteId = UMAMI_SITE_ID
    s.dataset.hostUrl = `${window.location.origin}/u`
    document.head.appendChild(s)
  }
}

export const analytics = {
  identify(id: string, props: Record<string, unknown>) {
    // Both gated on _initialized, which is only true after consent — the Umami
    // call previously fired regardless, identifying users who hadn't opted in.
    if (!_initialized) return
    posthog.identify(id, props)
    window.umami?.identify({ userId: id, ...props })
  },
  reset() {
    if (_initialized) posthog.reset()
  },
  track(event: string, props?: Record<string, unknown>) {
    if (_initialized) posthog.capture(event, props)
    const source = referralSource()
    window.umami?.track(event, source ? { referral_source: source, ...props } : props)
  },
}
